import type { GetStaticPaths, GetStaticPathsResult, GetStaticProps } from 'next'
import { withPasswordProtect } from 'next-password-protect'
import type { FC } from 'react'

import type { HomePage } from '@/types/homepage'
import { type WebsiteLocale, HOMEPAGE_LOCALES } from '@/types/locales'

import { getHomePageContent } from '@/graphql/Homepage'
import {
  getAllModularLandingPages,
  getModularLandingPageBySlug,
  type ModularLandingPageRecord,
} from '@/graphql/ModularLandingPage'

import { Homepage } from '@/components/pages/homepage/Homepage'
import { ModularLandingPage } from '@/components/pages/modular-landing/ModularLandingPage'

type ProtectedOrNotPageProps =
  | { type: 'homepage'; page: HomePage }
  | { type: 'modular'; page: ModularLandingPageRecord }

const ProtectedOrNotPage: FC<ProtectedOrNotPageProps> = (props) => {
  switch (props.type) {
    case 'homepage': {
      return <Homepage page={props.page} />
    }

    case 'modular': {
      const PageComponent = props.page.passwordProtected
        ? (withPasswordProtect(ModularLandingPage) as unknown as typeof ModularLandingPage)
        : ModularLandingPage
      return <PageComponent page={props.page} />
    }

    default: {
      props satisfies never
      return null
    }
  }
}

export default ProtectedOrNotPage

type PathParams = { locale: string }

export const getStaticProps: GetStaticProps<ProtectedOrNotPageProps, PathParams> = async ({
  params,
  preview = false,
}) => {
  const localeOrSlug = params?.locale
  if (!localeOrSlug) throw new Error('No locale or slug provided')

  if (HOMEPAGE_LOCALES.includes(localeOrSlug as WebsiteLocale)) {
    const page = await getHomePageContent(localeOrSlug as WebsiteLocale, preview)
    if (page) {
      return { props: { type: 'homepage', page } }
    }
    throw new Error('No Homepage content found')
  }

  // Otherwise, show the modular landing page
  const page = await getModularLandingPageBySlug(localeOrSlug, preview)
  if (page) {
    return { props: { type: 'modular', page } }
  }

  return { notFound: true }
}

export const getStaticPaths: GetStaticPaths<PathParams> = async () => {
  const { allModularLandingPages } = await getAllModularLandingPages()

  const paths: GetStaticPathsResult<PathParams>['paths'] = [
    // Add homepage paths for each locale
    ...HOMEPAGE_LOCALES.map((locale) => ({ params: { locale } })),

    // Add modular landing page paths without locale
    ...allModularLandingPages.map((page) => ({ params: { locale: page.slug } })),
  ]

  return {
    paths,
    fallback: false,
  }
}
